import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Users, ExternalLink } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";

const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

export default function DashboardEquipe() {
  const navigate = useNavigate();
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/equipe", { params: { mes, ano } }).then((r) => setData(r.data));
  }, [mes, ano]);

  const corPct = (p) => (p >= 100 ? "#16a34a" : p >= 70 ? "#F97316" : "#dc2626");

  const dadosGrafico = (data?.vendedores || []).map((v) => ({
    vendedor: (v.vendedor_nome || "").split("@")[0],
    Contatos: v.percentual.contatos,
    Visitas: v.percentual.visitas,
    "Lev. Proposta": v.percentual.propostas,
    Análise: v.percentual.analise ?? 0,
    Contrato: v.percentual.contratos ?? 0,
    Fechamento: v.percentual.fechamentos,
  }));

  return (
    <div className="space-y-5" data-testid="dashboard-equipe-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Users size={28} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Painel da Equipe</h1>
            <p className="text-sm text-muted-foreground">Atingimento de metas de todos os vendedores · {MESES[mes - 1]}/{ano}</p>
          </div>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-xs">Mês</Label>
            <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
              <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
              <SelectContent>{MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Ano</Label>
            <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
              <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[hoje.getFullYear() - 1, hoje.getFullYear(), hoje.getFullYear() + 1].map((a) => (
                  <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Card className="p-5">
        <h2 className="text-lg font-bold mb-3" style={{ color: "#1B3A6B" }}>% Atingimento por vendedor</h2>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={dadosGrafico} layout="vertical" margin={{ left: 30, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 150]} />
              <YAxis dataKey="vendedor" type="category" width={120} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Contatos" fill="#60a5fa" />
              <Bar dataKey="Visitas" fill="#fbbf24" />
              <Bar dataKey="Propostas" fill="#a78bfa" />
              <Bar dataKey="Fechamentos" fill="#16a34a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="text-lg font-bold mb-3" style={{ color: "#1B3A6B" }}>Detalhes por vendedor</h2>
        <div className="space-y-3" data-testid="lista-equipe">
          {(data?.vendedores || []).map((v) => (
            <div key={v.vendedor_id} className="p-4 rounded border" data-testid={`vend-row-${v.vendedor_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-bold" style={{ color: "#1B3A6B" }}>{v.vendedor_nome}</div>
                  <div className="text-xs text-muted-foreground">{v.vendedor_email}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate(`/app/meu-dashboard?vendedor_id=${v.vendedor_id}`)}>
                  <ExternalLink size={12} className="mr-1" /> Detalhes
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {["contatos", "visitas", "propostas", "analise", "contratos", "fechamentos"].map((etapa) => {
                  const real = v.realizado[etapa] ?? 0;
                  const meta = v.meta["meta_" + etapa] ?? 0;
                  const pct = v.percentual[etapa] ?? 0;
                  const label = etapa === "propostas" ? "Lev. Proposta" : etapa === "fechamentos" ? "Fechamento" : etapa === "contratos" ? "Contrato" : etapa.charAt(0).toUpperCase() + etapa.slice(1);
                  return (
                    <div key={etapa}>
                      <div className="text-xs uppercase text-muted-foreground">{label}</div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg font-bold" style={{ color: corPct(pct) }}>{real}</span>
                        <span className="text-xs text-muted-foreground">/ {meta}</span>
                        <span className="text-xs ml-auto font-medium" style={{ color: corPct(pct) }}>{pct}%</span>
                      </div>
                      <Progress value={Math.min(100, pct)} className="h-1.5 mt-1" />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {(!data || data.vendedores.length === 0) && (
            <div className="text-sm text-muted-foreground py-8 text-center">Nenhum vendedor cadastrado.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
